import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import { type HTMLAttributes, type ReactNode, forwardRef, useMemo } from 'react';

import { cn } from '../utils/cn';

export interface BreadcrumbItem {
  /**
   * Label for the breadcrumb item
   */
  label: string;

  /**
   * URL for the breadcrumb item
   */
  href?: string;

  /**
   * Whether this is the current page
   */
  current?: boolean;

  /**
   * Icon to display before the label
   */
  icon?: ReactNode;
}

export interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
  /**
   * Breadcrumb items
   */
  items: BreadcrumbItem[];

  /**
   * Separator icon
   * @default ChevronRightIcon
   */
  separator?: ReactNode;

  /**
   * Show home icon for first item
   * @default false
   */
  showHomeIcon?: boolean;

  /**
   * Maximum items to show before collapsing
   * @default undefined (show all)
   */
  maxItems?: number;
}

/**
 * Breadcrumb component
 *
 * Navigation breadcrumbs showing the current page location within the site hierarchy.
 *
 * @example
 * ```tsx
 * <Breadcrumb
 *   items={[
 *     { label: 'Home', href: '/' },
 *     { label: 'Investments', href: '/investments' },
 *     { label: 'Edit', current: true },
 *   ]}
 *   showHomeIcon
 * />
 * ```
 */
export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  ({ items, separator, showHomeIcon = false, maxItems, className, ...props }, ref) => {
    const displayItems = useMemo(() => {
      if (!maxItems || items.length <= maxItems) {
        return items;
      }

      // Show first item, ellipsis, and last (maxItems - 1) items
      const firstItem = items[0];
      const lastItems = items.slice(-(maxItems - 1));

      return [firstItem, { label: '...', href: undefined, current: false }, ...lastItems];
    }, [items, maxItems]);

    const defaultSeparator = (
      <ChevronRightIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
    );

    return (
      <nav
        ref={ref}
        className={cn('flex items-center space-x-2 text-sm', className)}
        aria-label="Breadcrumb"
        {...props}
      >
        <ol className="flex items-center space-x-2">
          {displayItems.map((item, index) => {
            if (!item) {
              return null;
            }

            const isLast = index === displayItems.length - 1;
            const isFirst = index === 0;

            return (
              <li key={index} className="flex items-center space-x-2">
                {index > 0 && (
                  <span className="flex-shrink-0">{separator || defaultSeparator}</span>
                )}

                {item.href && !item.current ? (
                  <a
                    href={item.href}
                    className={cn(
                      'flex items-center gap-1.5 font-medium transition-colors',
                      'text-gray-600 hover:text-gray-900',
                      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded'
                    )}
                  >
                    {isFirst && showHomeIcon && <HomeIcon className="h-4 w-4" aria-hidden="true" />}
                    {item.icon && !showHomeIcon && item.icon}
                    {item.label}
                  </a>
                ) : (
                  <span
                    className={cn(
                      'flex items-center gap-1.5',
                      isLast || item.current ? 'font-semibold text-gray-900' : 'text-gray-500'
                    )}
                    aria-current={item.current || isLast ? 'page' : undefined}
                  >
                    {isFirst && showHomeIcon && <HomeIcon className="h-4 w-4" aria-hidden="true" />}
                    {item.icon && !showHomeIcon && item.icon}
                    {item.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }
);

Breadcrumb.displayName = 'Breadcrumb';
